import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, Tenant } from '../api/ApiClient';

function resolveFirebaseSuffix(tenant: Tenant): string {
  if (tenant.firebase_id) {
    return tenant.firebase_id;
  }
  return tenant.id;
}

function getUrls(tenant: Tenant): string[] {
  const urls = [];
  const firebaseSuffix = resolveFirebaseSuffix(tenant);

  urls.push(`https://${tenant.id}.s-cloud.app`);
  urls.push(`https://${tenant.id}.smart-st.app`);
  urls.push(`https://org-kamasu-eve-saas-${firebaseSuffix}.web.app`);

  return urls;
}

// テナントAPIのGAEランタイムがサポート期限内かの確認状態。
// checking=確認中(ボタン無効) / ok=期限内(ボタン有効) / ng=期限外・確認失敗(ボタン無効)
type RuntimeCheck = 'checking' | 'ok' | 'ng';

export default function TenantsPage() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [showDeletingDialog, setShowDeletingDialog] = useState<boolean>(false);
  const [deletingTenant, setDeletingTenant] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [runtimeCheck, setRuntimeCheck] = useState<RuntimeCheck>('checking');
  const navigate = useNavigate();

  const refreshTenants = async () => {
    const tenants = await apiClient.getTenants();
    const activeTenants = tenants.filter((t: any) => t.status !== 'deleted');
    setTenants(activeTenants);
  };

  const checkRuntimeStatus = async () => {
    try {
      const status = await apiClient.getRuntimeStatus();
      if (status.supported) {
        setRuntimeCheck('ok');
        return;
      }
      setRuntimeCheck('ng');
    } catch {
      // 確認できない場合も安全側に倒して作成を止める
      setRuntimeCheck('ng');
    }
  };

  useEffect(() => {
    refreshTenants();
    checkRuntimeStatus();
  }, []);

  const onRefreshTenantsClicked = () => {
    refreshTenants();
  };

  const onCreateTenantClicked = () => {
    navigate('/tenants/new');
  };

  const showStatus = (status: string): string => {
    switch (status) {
      case 'creating':
        return '作成中';
      case 'enabled':
        return '有効';
      case 'error':
        return 'エラー';
      case 'deleting':
        return '削除中';
      case 'delete_error':
        return '削除エラー';
      default:
        return '不明';
    }
  };

  const isDeletableStatus = (status: string): boolean => {
    const deletableStatuses = ['enabled', 'error', 'delete_error'];
    return deletableStatuses.includes(status);
  };

  const hasTenantInProgress = tenants.some((t) => t.status === 'creating' || t.status === 'deleting');

  const showDeleteDialog = (tenant: any) => {
    setDeletingTenant(tenant);
    setShowDeletingDialog(true);
  };

  const cancelDelete = () => {
    setShowDeletingDialog(false);
    setDeletingTenant(null);
  };

  const execDelete = async () => {
    setLoading(true);
    try {
      await apiClient.deleteTenant(deletingTenant.id);
      setShowDeletingDialog(false);
      setDeletingTenant(null);
    } catch (e) {
      alert('テナントの削除リクエストに失敗しました。他のテナントが作成中・削除中でないか確認してください。');
    } finally {
      setLoading(false);
    }
    await refreshTenants();
  };

  return (
    <Box sx={{ minWidth: '800px', maxWidth: '1200px', m: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        S Cloud管理画面 / テナント一覧
      </Typography>

      <Backdrop
        open={loading}
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.tooltip + 1,
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Dialog open={showDeletingDialog}>
        <DialogTitle>テナント削除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <div>
              テナント {deletingTenant?.name}（テナントID：{deletingTenant?.id}）を削除します。よろしいですか？
            </div>
            <div>
              <b>注意：この操作は巻き戻せません</b>
            </div>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={execDelete} color="error">
            削除する
          </Button>
          <Button onClick={cancelDelete}>キャンセル</Button>
        </DialogActions>
      </Dialog>

      <Box>
        <Button onClick={onRefreshTenantsClicked} sx={{ mr: 2 }}>
          一覧を更新する
        </Button>
        <Button disabled={runtimeCheck !== 'ok' || hasTenantInProgress} onClick={onCreateTenantClicked}>
          テナントを作成する
        </Button>
      </Box>

      {runtimeCheck === 'checking' && <Box>サポート状態を確認中...</Box>}

      {runtimeCheck === 'ng' && (
        <Box sx={{ color: 'error.main' }}>ランタイムに問題があります。開発者に連絡してください。</Box>
      )}

      {hasTenantInProgress && (
        <Box>テナントの作成中・削除中は、追加の作成・削除ができません。完了までお待ち下さい。</Box>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>テナントID</TableCell>
              <TableCell>ステータス</TableCell>
              <TableCell>テナント名</TableCell>
              <TableCell>URL</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tenants.map((t) => (
              <TableRow key={t.id}>
                <TableCell>{t.id}</TableCell>
                <TableCell>{showStatus(t.status)}</TableCell>
                <TableCell>{t.name}</TableCell>
                <TableCell>
                  {getUrls(t).map((url) => (
                    <div key={url}>
                      <Link href={url} target="_blank" rel="noreferrer" underline="none">
                        {url}
                      </Link>
                    </div>
                  ))}
                </TableCell>
                <TableCell>
                  <Button
                    disabled={!isDeletableStatus(t.status) || hasTenantInProgress}
                    color="error"
                    onClick={() => showDeleteDialog(t)}
                  >
                    削除
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
